<template>
  <div class="select-chart-container" :id="id">
    <div class="position-absolute" style="top: 15px; right: 20px">
      <DownloadChartButton :id="id" />
    </div>
    <v-row>
      <v-col cols="12">
        <h5 v-if="title">{{ title }}</h5>
      </v-col>
      <v-col cols="12">
        <AverageScore
          colorTable="gradientColors"
          :extra-legend-items="annotations.map(a => a.label)"
          :averageScoreOption="true"
          legendItemTitle="PPI"
          :gradientColors="gradientColors"
        />
      </v-col>

      <v-col cols="12">
        <BaseBarChart
          style="height: 1200px;"
          v-if="finalChartData"
          :chartData="finalChartData"
          :annotations="annotations"
          :options="chart.options"
          :aspect-ratio="aspectRatio"
          class="bar-chart-plot-full-width"
        />
      </v-col>
    </v-row>
  </div>
</template>

<style scoped>
  h5 {
    text-align: center;
    font-size: 22px;
    width: 80%;
    margin: auto;
  }
</style>

<script setup>
// This file includes a custom title and custom legend
// for bar charts
import BaseBarChart from "../base/BaseBarChart.vue";
import ChartTitle from "../components/ChartTitle.vue";
import AverageScore from "../components/AverageScore.vue";
import { computed } from "vue";
import Utils from "@/Mixins/Utils";
import DownloadChartButton from "@/Charts/components/DownloadChartButton.vue";
import { horizontalBarOptions } from "@/Charts/chartOptions";

const props = defineProps({
  title: {
    type: String,
    default: null,
    required: false,
  },
  chartData: {
    type: Object,
    required: true,
  },
  gradientColors: {
    type: Array,
    required: true,
  },
  annotations: {
    type: Array,
    required: false,
    default: () => {
      return []
    }
  },
  axisLabels: {
    type: Object,
    required: false,
    default() {
      return { x: "", y: "" };
    },
  },
  id: {
    type: String,
    required: false,
  },
});

const finalChartData = computed(() => {
  const result = {}
  result.labels = []

  for (let label of props.chartData.labels) {
    result.labels.push(formatLabel(label, 75))
  }

  for (let dataset of props.chartData.datasets) {
    dataset.barThickness = 15
    dataset.barPercentage = 0.7
    dataset.categoryPercentage = 0.8
  }

  result.datasets = props.chartData.datasets
  return result
})

/**
 * Takes a string phrase and breaks it into separate phrases
 * no bigger than 'maxWidth', breaks are made at complete words.
 */
function formatLabel(str, maxWidth) {
  const sections = [];
  const words = str.split(" ");

  let temp = "";

  words.forEach(function(item, index) {
    if (temp.length > 0)
    {
      const concat = temp + ' ' + item;

      if (concat.length > maxWidth) {
        sections.push(temp);
        temp = "";
      }
      else {
        if (index === (words.length-1)) {
          sections.push(concat);
          return;
        }
        else {
          temp = concat;
          return;
        }
      }
    }

    if (index === (words.length-1)) {
      sections.push(item);
      return;
    }

    if (item.length < maxWidth) {
      temp = item;
    }
    else {
      sections.push(item);
    }

  });

  return sections;
}

const aspectRatio = computed(() => {
  return Utils.methods.psiChartAspectRatio(props.chartData.labels.length);
});

const chart = computed(() => {
  return {
    animation: { duration: 1 },
    options: horizontalBarOptions({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 10,
          right: 10,
        }
      },
    }),
  };
});
</script>